import { Table } from '@acceso/design-system';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VideoPresencesSubTable } from '@/components/VideoPresences/components/VideoPresencesSubTable/VideoPresencesSubTable';
import { useVideoPresencesDatatable } from '@/components/VideoPresences/hooks/use-video-presences-datatable';
import { VideoPresencesManagementBar } from '@/components/VideoPresences/VideoPresencesManagementBar';
import {
  PeoplePresenceDropdown,
  useBroadcastScheduleContext,
} from '@/components/VideoPresencesInlineControls';
import { buildNewPeopleDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  FrameActionType,
  TBroadcastScheduleOnUpdateInline,
  VISUALIZATION_OPTIONS_VALUES,
  VisualizationOption,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TPostSegmentPeople, TSegmentPeople } from '@/features/segments/types';
import { useGroupedPresences } from '@/hooks/use-grouped-presences';
import { useSegmentPresences } from '@/hooks/use-segment-presences';
import { PresenceTypeEnum } from '@/types/presences';

type TBroadcastScheduleTableProps = {
  items: TSegmentPeople[];
  onPreview?: (item: TSegmentPeople, frame?: FrameActionType) => void;
  onUpdate?: (item: TSegmentPeople) => void;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<TPostSegmentPeople>;
  onDelete?: (item: TSegmentPeople, silently?: boolean) => void;
};

export function BroadcastScheduleProgramBodySegmentToolkitPeopleTable({
  items,
  onPreview,
  onUpdate,
  onUpdateInline,
  onDelete,
}: TBroadcastScheduleTableProps) {
  const { t } = useTranslation();
  const { groupedPresences, groupVisualization } = useBroadcastScheduleContext();
  const { personaData } = useBroadcastScheduleProgram();
  const { buildGroupOptions } = useSegmentPresences();

  const { getRowClassStyles, peopleById } = useGroupedPresences(items, onPreview, 'people_id');

  const renderEntityCell = useCallback(
    (item: TSegmentPeople, showSubArrows: (f: (v: boolean) => boolean) => void) => (
      <PeoplePresenceDropdown
        item={item}
        peopleById={peopleById}
        onPreview={onPreview}
        onUpdateInline={onUpdateInline}
        parentCallback={() => showSubArrows(prev => !prev)}
      />
    ),
    [onPreview, onUpdateInline, peopleById]
  );

  const {
    getPeopleOptions: getOptions,
    getPeopleColumns,
    getLogosThumbsColumns,
  } = useVideoPresencesDatatable({
    items,
    onPreview,
    onUpdateInline,
    onUpdate,
    onDelete,
    entityIdKey: 'people_id',
    showSupportColumn: false,
    buildDataToSave: buildNewPeopleDataToSave,
    presenceData: personaData ?? undefined,
    searchBarPlaceholder: t('Buscar por persona'),
    renderEntityCell,
  });

  const hasCustomExtraBar = !!onPreview;

  const columnsByVisualization: Record<VisualizationOption, typeof getPeopleColumns> = {
    [VISUALIZATION_OPTIONS_VALUES.List]: getPeopleColumns,
    [VISUALIZATION_OPTIONS_VALUES.Thumbnails]: getLogosThumbsColumns,
  };

  const renderAdditionalRowForList = useCallback(
    (item: TSegmentPeople) => (
      <VideoPresencesSubTable
        item={item as TSegmentPeople & { children: TSegmentPeople[] }}
        columns={getPeopleColumns}
        onPreview={onPreview}
        onUpdate={onUpdate}
        onUpdateInline={onUpdateInline}
        onDelete={onDelete}
        showSupportColumn={false}
        renderEntityCell={child => (
          <PeoplePresenceDropdown
            item={child}
            peopleById={peopleById}
            onPreview={onPreview}
            onUpdateInline={onUpdateInline}
            hasParent
          />
        )}
        entityIdKey="people_id"
        buildDataToSave={buildNewPeopleDataToSave as any}
        presenceData={personaData ?? undefined}
        activeEntityId={personaData?.id}
        tableDataId="people-subtable"
      />
    ),
    [getPeopleColumns, onPreview, onUpdate, onUpdateInline, onDelete, personaData, peopleById]
  );

  const additionalRowByVisualization: Partial<
    Record<VisualizationOption, (item: TSegmentPeople) => React.ReactNode>
  > = {
    [VISUALIZATION_OPTIONS_VALUES.List]: renderAdditionalRowForList,
  };

  const groupOptions = useMemo(
    () => buildGroupOptions(PresenceTypeEnum.PEOPLE),
    [buildGroupOptions]
  );

  const customExtraBar = useMemo(
    () =>
      hasCustomExtraBar ? (
        <VideoPresencesManagementBar<TPostSegmentPeople>
          {...{ onDelete, onUpdateInline, items, onPreview }}
          groupOptions={groupOptions}
          presenceType={PresenceTypeEnum.PEOPLE}
        />
      ) : null,
    [hasCustomExtraBar, onDelete, onUpdateInline, items, onPreview, groupOptions]
  );

  if (!groupedPresences) return null;

  return (
    <Table<any>
      containerClassName="!overflow-x-hidden"
      className="broadcast-schedule-program-body-segment-toolkit-people-table !w-auto min-w-full table-fixed"
      options={getOptions}
      columns={columnsByVisualization[groupVisualization] ?? getPeopleColumns}
      data={groupedPresences}
      renderAdditionalRow={additionalRowByVisualization[groupVisualization]}
      customExtraBar={customExtraBar}
      ignoreWrap
      getSpecialClassNames={getRowClassStyles}
    />
  );
}
